import { useState, type ReactNode } from 'react';
import {
  deleteMyWebConnectorSecret,
  describeError,
  issueMyWebConnectorSecret,
  problemErrorCode,
  problemStatus,
  type OperatorSelfAccess,
  type StepUpProof,
} from '@/lib/api';
import {
  claudeCodeAddCommand,
  claudeCodeLoginCommand,
  claudeCodeServerName,
  operatorEnvironmentTag,
  operatorMcpUrl,
} from '@/lib/operator-access';
import { useStepUpDialog } from '@/lib/step-up';
import { stepUpTarget } from '@/lib/passkeys';
import { useAuthStore } from '@/stores/auth';
import { CopyButton } from './CopyButton';
import { StepUpDialog } from './StepUpDialog';

/**
 * "Conectar ao Claude" em `/profile` (change platform-mcp-operator-access, OA-D12): o titular, de
 * qualquer papel, conecta o MCP do backoffice, só de leitura, pelo login nativo do Claude Code ou do
 * claude.ai, com o `client_id` pré-registrado do próprio cliente `Operator`.
 *
 * O cartão monta só textos: a URL do MCP do operador vem da tag do `client_id` (o mesmo host que a
 * API deriva), e os comandos e passos são instrução. Quem decide é a API: o pedido de autorização, a
 * volta e o consentimento passam por `/mcp/autorizar`.
 *
 * O segredo do conector web (gerar, girar, apagar) é do titular: gerar e girar pedem o step-up
 * (propósito `operator_web_connector_secret.issue`, alvo a própria conta) e o segredo aparece uma vez,
 * só na memória deste componente, até "Já copiei". Nunca vai para storage, log ou analytics, nem
 * volta da API depois (o `GET` só diz se existe e quando foi gerado).
 */

export const CONNECT_TO_CLAUDE_TITLE = 'Conectar ao Claude';
export const NOT_ENABLED_TEXT =
  'Sua conta não está habilitada para conectar o Claude neste ambiente. Peça a um Admin para habilitar você em Agentes de IA (/agentes).';
export const ROTATE_WARNING =
  'Girar o segredo desconecta o claude.ai na hora: cole o segredo novo no conector e conecte de novo.';
export const SECRET_ONCE_WARNING =
  'Copie agora: o segredo aparece só esta vez e não fica guardado no backoffice. Perdeu? Gere outro.';
export const DELETE_CONFIRM_TEXT =
  'Apagar o segredo do conector web? O claude.ai desconecta na hora e não conecta de novo até você gerar outro. O Claude Code não é afetado.';

const ERROR_MESSAGES: Record<string, string> = {
  'platform.agent_client.operator_disabled':
    'O acesso pessoal ao MCP está desligado neste ambiente: nenhum segredo novo é gerado agora.',
  'platform.agent_client.not_enabled': 'O seu cliente está desabilitado neste ambiente. Peça a um Admin em Agentes de IA.',
  'platform.agent_client.not_found': 'Sua conta não tem cliente pessoal neste ambiente. Peça a um Admin em Agentes de IA.',
};

function describeSecretError(err: unknown): string {
  const code = problemErrorCode(err);
  if (code && Object.hasOwn(ERROR_MESSAGES, code)) return ERROR_MESSAGES[code];
  if (problemStatus(err) === 404 && !code) return 'Recurso indisponível nesta versão da API.';
  return describeError(err);
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function ConnectToClaudeCard({
  access,
  onChanged,
}: {
  access: OperatorSelfAccess;
  /** Relê `GET /platform/me/operator-access` depois de gerar ou apagar o segredo. */
  onChanged: () => void;
}) {
  const operatorId = useAuthStore((s) => s.operator?.id ?? null);
  // O segredo recém-gerado: só aqui, em memória, até "Já copiei" ou sair da página.
  const [issuedSecret, setIssuedSecret] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ tone: 'info' | 'error'; text: string } | null>(null);

  const { client } = access;
  const secretState = client?.webConnectorSecret ?? null;
  const hasSecret = secretState?.present === true;

  async function issue(proof: StepUpProof) {
    const issued = await issueMyWebConnectorSecret(proof);
    setIssuedSecret(issued.secret);
    setNotice({
      tone: 'info',
      text: issued.revokedGrantId
        ? 'Segredo novo gerado. A conexão do claude.ai foi desfeita: cole o segredo novo no conector e conecte de novo.'
        : 'Segredo gerado.',
    });
    onChanged();
  }

  const stepUp = useStepUpDialog<null>(
    (proof) => issue(proof),
    (err) => setNotice({ tone: 'error', text: describeSecretError(err) }),
    {
      passkeyTarget: () => (operatorId ? stepUpTarget('operatorWebConnectorSecretIssue', operatorId) : null),
    },
  );

  async function removeSecret() {
    setBusy(true);
    setNotice(null);
    try {
      await deleteMyWebConnectorSecret();
      setIssuedSecret(null);
      setConfirmingDelete(false);
      setNotice({ tone: 'info', text: 'Segredo apagado. O claude.ai não conecta mais até você gerar outro.' });
      onChanged();
    } catch (err) {
      setConfirmingDelete(false);
      setNotice({ tone: 'error', text: describeSecretError(err) });
    } finally {
      setBusy(false);
    }
  }

  function startIssue() {
    if (stepUp.open || busy) return;
    setNotice(null);
    setIssuedSecret(null);
    stepUp.start(null);
  }

  const tag = operatorEnvironmentTag(client?.oauthClientId);
  const usable = client !== null && client.status === 'Enabled' && access.enabled && tag !== null;

  return (
    <section className="card" aria-label={CONNECT_TO_CLAUDE_TITLE} data-testid="connect-to-claude">
      <h3 style={{ fontSize: 16 }}>{CONNECT_TO_CLAUDE_TITLE}</h3>
      <p className="muted" style={{ fontSize: 14 }}>
        Use o MCP do backoffice, só de leitura, no Claude Code deste computador ou no claude.ai. Cada conexão é
        aprovada por você em /mcp/autorizar, com o código do autenticador. Uma conexão ativa por vez: conectar o
        claude.ai substitui a do Claude Code, e vice-versa.
      </p>

      {notice && (
        <div className={notice.tone === 'error' ? 'error-banner' : 'info-banner'} role={notice.tone === 'error' ? 'alert' : 'status'}>
          {notice.text}
        </div>
      )}

      {client === null ? (
        <p className="muted" style={{ fontSize: 14 }} data-testid="connect-to-claude-not-enabled">
          {NOT_ENABLED_TEXT}
        </p>
      ) : !access.enabled ? (
        <div className="info-banner" role="status">
          O acesso pessoal ao MCP está desligado neste ambiente: nenhuma conexão nova é aceita agora.
        </div>
      ) : client.status !== 'Enabled' ? (
        <p className="muted" style={{ fontSize: 14 }} data-testid="connect-to-claude-disabled">
          O seu cliente está desabilitado neste ambiente. Peça a um Admin para habilitar você de novo em Agentes de IA
          (/agentes).
        </p>
      ) : tag === null ? (
        <p className="muted" style={{ fontSize: 14 }} data-testid="connect-to-claude-unknown-client">
          O identificador do seu cliente não está no formato esperado. Peça a um Admin para conferir em Agentes de IA.
        </p>
      ) : null}

      {usable && client && tag && (
        <>
          <dl className="detail-grid" style={{ margin: '0 0 12px' }}>
            <dt className="k" style={{ margin: 0 }}>URL do MCP</dt>
            <dd style={{ margin: 0, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span className="mono" data-testid="operator-mcp-url" style={{ wordBreak: 'break-all' }}>
                {operatorMcpUrl(tag)}
              </span>
              <CopyButton value={operatorMcpUrl(tag)} label="Copiar a URL do MCP" iconOnly />
            </dd>
            <dt className="k" style={{ margin: 0 }}>Client ID</dt>
            <dd style={{ margin: 0, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span className="mono" style={{ wordBreak: 'break-all' }}>
                {client.oauthClientId}
              </span>
              <CopyButton value={client.oauthClientId} label="Copiar o client ID" iconOnly />
            </dd>
          </dl>

          <Block title="Claude Code">
            <ol style={{ margin: '0 0 0 18px', padding: 0, fontSize: 14 }}>
              <li>
                No terminal, adicione o servidor (uma vez por computador):
                <Command value={claudeCodeAddCommand(client.oauthClientId, tag)} label="Copiar o comando de adicionar" />
              </li>
              <li>
                Entre (ou, dentro do Claude Code, <span className="mono">/mcp</span> → Authenticate):
                <Command value={claudeCodeLoginCommand(tag)} label="Copiar o comando de login" />
              </li>
              <li>
                O navegador abre a autorização: confira o destino "Claude Code neste computador" e aprove com o código
                do autenticador.
              </li>
            </ol>
            <p className="muted" style={{ fontSize: 13, margin: '8px 0 0' }}>
              A renovação vale por algumas horas depois de cada aprovação; quando o Claude Code avisar que precisa
              entrar de novo, rode o login outra vez. O servidor fica como{' '}
              <span className="mono">{claudeCodeServerName(tag)}</span>, só no seu usuário.
            </p>
          </Block>

          <Block title="claude.ai (web, desktop e celular)">
            <ol style={{ margin: '0 0 0 18px', padding: 0, fontSize: 14 }}>
              <li>Gere o segredo do conector web abaixo (uma vez; girar desconecta o claude.ai).</li>
              <li>No claude.ai: Configurações → Conectores → Adicionar conector personalizado.</li>
              <li>
                URL: <span className="mono">{operatorMcpUrl(tag)}</span>.
              </li>
              <li>
                Em "Use your own OAuth client", informe o client ID <span className="mono">{client.oauthClientId}</span> e
                o segredo do conector web.
              </li>
              <li>
                Conectar: confira o destino "claude.ai (conector web)" na autorização e aprove com o código do
                autenticador.
              </li>
            </ol>
          </Block>
        </>
      )}

      {client !== null && (usable || hasSecret) && (
        <Block title="Segredo do conector web">
          <p style={{ fontSize: 14, margin: '0 0 8px' }} data-testid="web-connector-secret-state">
            {hasSecret
              ? `Gerado em ${formatDateTime(secretState?.issuedAt)}.`
              : 'Nenhum segredo gerado: sem ele, o claude.ai não conecta.'}
          </p>

          {issuedSecret && (
            <div className="warning-banner" role="region" aria-label="Segredo gerado" style={{ marginBottom: 8 }}>
              <p style={{ margin: '0 0 6px', fontSize: 13 }}>{SECRET_ONCE_WARNING}</p>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <code className="mono" data-testid="web-connector-secret-value" style={{ wordBreak: 'break-all' }}>
                  {issuedSecret}
                </code>
                <CopyButton value={issuedSecret} label="Copiar o segredo do conector web" />
              </div>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                style={{ marginTop: 8 }}
                onClick={() => setIssuedSecret(null)}
              >
                Já copiei
              </button>
            </div>
          )}

          {usable && hasSecret && (
            <p className="muted" style={{ fontSize: 13, margin: '0 0 8px' }}>
              {ROTATE_WARNING}
            </p>
          )}

          {confirmingDelete ? (
            <div role="group" aria-label="Confirmar apagar o segredo">
              <p style={{ fontSize: 13, margin: '0 0 8px' }}>{DELETE_CONFIRM_TEXT}</p>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="btn btn-danger btn-sm" disabled={busy} onClick={() => void removeSecret()}>
                  {busy ? 'Apagando…' : 'Sim, apagar'}
                </button>
                <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => setConfirmingDelete(false)}>
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {usable && (
                <button type="button" className="btn btn-sm" disabled={busy || stepUp.open} onClick={startIssue}>
                  {hasSecret ? 'Girar segredo' : 'Gerar segredo'}
                </button>
              )}
              {hasSecret && (
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  disabled={busy || stepUp.open}
                  onClick={() => {
                    setNotice(null);
                    setConfirmingDelete(true);
                  }}
                >
                  Apagar segredo
                </button>
              )}
            </div>
          )}
        </Block>
      )}

      {stepUp.open && (
        <StepUpDialog
          title={hasSecret ? 'Girar o segredo do conector web' : 'Gerar o segredo do conector web'}
          description={
            <>
              {hasSecret && <p style={{ margin: '0 0 8px' }}>{ROTATE_WARNING}</p>}
              <p style={{ margin: 0 }}>
                O segredo aparece uma vez só, nesta página. Confirme com o código atual do autenticador. Recovery code
                não vale aqui.
              </p>
            </>
          }
          busy={stepUp.busy}
          error={stepUp.error}
          onSubmit={stepUp.submit}
          onCancel={stepUp.cancel}
          onPasskey={stepUp.passkeyAvailable ? stepUp.submitPasskey : undefined}
        />
      )}
    </section>
  );
}

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section
      aria-label={title}
      style={{ borderTop: '1px solid var(--color-border, #e2e8f0)', paddingTop: 12, marginTop: 12 }}
    >
      <h4 style={{ fontSize: 14, margin: '0 0 8px' }}>{title}</h4>
      {children}
    </section>
  );
}

function Command({ value, label }: { value: string; label: string }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', margin: '6px 0 8px' }}>
      <pre className="mono" style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all', flex: 1, fontSize: 13 }}>
        {value}
      </pre>
      <CopyButton value={value} label={label} iconOnly />
    </div>
  );
}
